/**
 * Schema -> DOM renderer.
 *
 * Ported from the rendering half of `smc-carbon-survey-question-builder.html`
 * (buildTree ~line 1645, renderForm/renderSection/renderNode/renderQuestion/buildControl
 * ~lines 1770-2385, the update() cycle ~lines 2386-2577). The Question-Builder authoring
 * UI (structure editing, lint panel, choice editor) is explicitly out of scope per
 * CLAUDE.md / farmer-form-versioning (P2) and was not ported.
 *
 * Behavioural contract kept from the prototype (see farmer-form-engine-frontend skill):
 *  - Render strictly from the given schema (the submission's `formSchemaSnapshot`),
 *    never from a live "active form" fetch.
 *  - `relevant`/`calculate` semantics identical to the original evalRelevant/evalExpr
 *    (imported from engine/relevance.js).
 *  - Table Matrix display mode for repeats/groups with `ui_display === 'table'`.
 *  - `select_one` renders as a dropdown by default and as radio buttons when
 *    `appearance` says so — the old prototype only had the dropdown, so an empty
 *    `appearance` must keep meaning "dropdown" (see engine/question-type.js).
 *  - Repeat row removal reindexes keys (engine/matrix.js) instead of just truncating.
 *  - Readonly is NOT handled here — see ui/readonly.js, applied as a single DOM sweep
 *    after mount, per skill §8 ("một chỗ duy nhất").
 *  - `type: image` (or any attachment-like type) renders a disabled placeholder — object
 *    storage / attachments are out of scope (CLAUDE.md, skill §5).
 */
import {
  buildSurveyTree,
  indexChoices,
  isDynamicList,
  DEFAULT_CALL_RE,
  MASTER_DATA_KEYS,
  maxRowsOf,
  splitLabelNumber,
  surveySectionId,
  surveyColumnLabel,
  repeatHasNamedRows,
  repeatRowLabel,
} from '@farmer/audit-rules';
import {
  evalExpr,
  evalRelevant,
  checkConstraint,
  constraintMax,
  resolve,
  choiceAllowed,
  legacyChoiceFilterVar,
  listOfQuestions,
} from './relevance.js';
import { countInstances, removeRepeatRow, clampAutoCount, MAX_INSTANCES } from './matrix.js';
import { buildPrefix } from './keys.js';
import { paperSelectMode, selectOneMode, shouldShowOptionSearch } from './question-type.js';
import { triggerName } from './prefill.js';

const AUTO_TYPES = ['start', 'end', 'start-geopoint', 'today', 'deviceid', 'audit'];
const NUMERIC_TYPES = new Set(['integer', 'decimal']);
/** Ô nhập được của một ô bảng: bỏ ô tự tính và ô đã khoá. */
const FOCUSABLE_CELL = 'input[data-key]:not([readonly]):not([disabled]), select[data-key]:not([disabled])';
const isFilled = (control) =>
  control.type === 'checkbox' || control.type === 'radio' ? control.checked : String(control.value).trim() !== '';
const ATTACHMENT_TYPES = new Set(['image', 'file', 'audio', 'video']);

// Legacy table-display defaults ported from applyExplicitDisplayConfiguration() in the
// prototype (~line 1476): when a repeat's `ui_display` isn't set explicitly, these named
// parent repeats default to Table Matrix rather than stacked cards, matching the UX that
// was already designed/approved for the onfarm & removal forms.
const LEGACY_TABLE_REPEAT_PARENTS = {
  onfarm: new Set([
    'intercrop_tree', 'shade_tree', 'crop_residues', 'fertilizers',
    'pesticides', 'energy', 'machine', 'watering', 'transport',
  ]),
  removal: new Set(['plot_information']),
};

function el(tag, className, html) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (html !== undefined) n.innerHTML = html;
  return n;
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

const splitNum = splitLabelNumber;

const selectedList = (v) => String(v || '').trim().split(/\s+/).filter(Boolean);

/** Bỏ dấu tiếng Việt + hạ chữ thường, để gõ "phan bon" vẫn tìm ra "phân bón". */
export function normalizeSearch(value) {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Chuỗi dùng để tìm kiếm của một nút và toàn bộ con của nó — gộp nhãn, gợi ý và
 * tên biến. Câu hỏi nằm trong bảng không có khối `.q` riêng, nên khối bảng/nhóm
 * lặp phải mang theo nội dung của các cột thì tìm kiếm mới thấy được chúng.
 */
function searchTextOf(node) {
  const parts = [node.label, node.hint, node.name];
  (node.children || []).forEach((c) => parts.push(searchTextOf(c)));
  return normalizeSearch(parts.filter(Boolean).join(' '));
}

/**
 * Cây câu hỏi dùng để render. Uỷ quyền cho `buildSurveyTree` của @farmer/audit-rules vì
 * server cũng dựng đúng cây này khi kiểm tra câu bắt buộc lúc nộp phiếu — hai bản dựng
 * cây khác nhau nghĩa là hai cách sinh `prefix` nhóm lặp khác nhau, tức lệch luôn khoá
 * `answers` giữa màn hình và điều kiện chặn.
 */
export const buildTree = buildSurveyTree;

function displayModeOf(node) {
  const raw = String(node.ui_display || '').trim();
  if (raw === 'table') return 'table';
  return node.kind === 'repeat' ? 'cards' : 'list';
}

/**
 * @param {HTMLElement} container
 * @param {{survey: any[], choices: any[]}} schema - a submission's formSchemaSnapshot
 * @param {import('./state.js').createState extends (...a:any)=>infer R ? R : never} state
 * @param {string} formKey - một khoá của FORM_KEYS (@farmer/audit-rules)
 * @param {Record<string,string>} [masterData] - the read-only Hộ/Vườn projection from the
 *   API (`submission.masterData`). Visible to `${...}` interpolation, `relevant` and
 *   `calculate` exactly like an answer, but never written back — R1 keeps these in
 *   `households`/`plots`, not in `submissions.answers`.
 * @param {Record<string, Array<{name: string, label: string}>>} [dynamicLists] - runtime
 *   choice lists (`__middlemen`) from `submission.dynamicLists`, keyed by list name.
 * @param {() => void} [onUpdate] - called after every recompute pass (mount, field change,
 *   repeat add/remove). Views use this to (re)schedule autosave and re-run the realtime
 *   audit panel. Cheap to call often — callers should debounce their own side effects.
 * @param {boolean} [paper] - màn nhập trên máy tính, giống phiếu giấy (Doc/11a): hiện số câu, lựa
 *   chọn ngắn hiện ngang, bảng giới hạn chiều cao. Chỉ đổi cách hiện, không đổi khoá `answers`.
 */
export function createFormRenderer({ container, schema, state, formKey, masterData, dynamicLists, onUpdate, paper = false }) {
  const master = masterData || {};
  const masterKeys = new Set(MASTER_DATA_KEYS[formKey] || []);
  // Everything that *reads* answers (relevant / calculate / ${} interpolation) goes
  // through this; everything that *writes* goes through `state` directly, so the Hộ/Vườn
  // layer can never leak into an autosave payload.
  //
  // Câu đang ẩn đọc ra RỖNG (ngữ nghĩa ODK, cùng `relevantValues` của @farmer/audit-rules):
  // A = Không ẩn B thì giá trị cũ của B không được giữ C (`${b} = '1'`) hiện hay ô tính từ B
  // ra số cũ. Rỗng chứ không xoá khoá — biến vắng hẳn thì đường đánh giá cũ coi là "không chặn".
  // Kể cả câu ẩn CHƯA từng trả lời: không đặt rỗng thì câu đứng sau nó vẫn hiện (câu mẹ ẩn,
  // câu con hiện — phiếu 6 g11_volume_m3).
  const readAnswers = () => {
    const out = Object.keys(master).length ? { ...state.all(), ...master } : state.all();
    for (const k of hiddenKeys) out[k] = '';
    return out;
  };
  // Danh sách động (`__middlemen`) không nằm trong sheet `choices` — API nạp theo đơn vị
  // của phiếu rồi trả kèm. Trộn vào đây thì mọi chỗ đọc `choices[list]` dùng được luôn.
  const choices = { ...indexChoices(schema.choices), ...(dynamicLists || {}) };
  // Biểu thức dạng mới (Doc/09 mục 3.3a) cần biết câu nào dùng list nào (`jr:choice-name`)
  // và cần chính bảng list (`choice-value`, `choice_filter` theo `name`). Đường cũ bỏ qua.
  const evalOpts = { listOf: listOfQuestions(schema.survey || []), lists: choices };
  const tree = buildTree(schema.survey);
  const manualCounts = new WeakMap(); // repeat wrapper element -> desired instance count
  let hiddenKeys = new Set();
  let uid = 0;
  /** Như `RELEVANCE_PASSES` của @farmer/audit-rules — dư cho chuỗi phụ thuộc thật, đứng yên thì thoát sớm. */
  const RELEVANCE_PASSES = 10;

  function fire() {
    if (typeof onUpdate === 'function') onUpdate();
  }

  // ---------------------------------------------------------------- mount
  function mount() {
    container.innerHTML = '';
    container.classList.add('survey-form');
    container.classList.toggle('is-paper', paper);
    tree.children.forEach((node) => {
      const n = renderTop(node);
      if (n) container.appendChild(n);
    });
    readAllInputs();
    recompute();
  }

  function renderTop(node) {
    if (AUTO_TYPES.includes(node.type)) return null;
    if (node.kind === 'group') return renderSection(node);
    if (node.kind === 'repeat') return renderRepeat(node, '');
    if (node.type === 'note') return renderNote(node, '');
    if (node.kind === 'question') return renderQuestionBlock(node, '', 0);
    return null;
  }

  // -------------------------------------------------------------- section
  function renderSection(node) {
    const { num, text } = splitNum(node.label);
    const sec = el('section', 'form-section');
    sec.id = surveySectionId(node);
    sec.dataset.sectionNum = num || '';
    sec.dataset.sectionText = text;
    sec.dataset.sectionSearch = searchTextOf(node);
    if (node.relevant) {
      sec.dataset.relevant = node.relevant;
      sec.dataset.prefix = '';
    }
    const head = el('button', 'form-section-head');
    head.type = 'button';
    head.setAttribute('aria-expanded', 'true');
    head.innerHTML = `<span class="fs-num">${esc(num || '')}</span>
      <span class="fs-title">${esc(text)}</span>
      <span class="fs-chevron ms">expand_more</span>`;
    head.addEventListener('click', () => {
      sec.classList.toggle('collapsed');
      head.setAttribute('aria-expanded', String(!sec.classList.contains('collapsed')));
    });
    sec.appendChild(head);
    const body = el('div', 'form-section-body');
    if (displayModeOf(node) === 'table') {
      renderGroupTableBody(node, body, '');
    } else {
      node.children.forEach((c) => {
        const n = renderNode(c, '', 0);
        if (n) body.appendChild(n);
      });
    }
    sec.appendChild(body);
    return sec;
  }

  function renderNode(node, prefix, idx) {
    if (AUTO_TYPES.includes(node.type)) return null;
    if (node.kind === 'repeat') return renderRepeat(node, prefix);
    if (node.kind === 'group') return renderGroup(node, prefix, idx);
    if (node.type === 'note') return renderNote(node, prefix);
    return renderQuestionBlock(node, prefix, idx);
  }

  function renderGroup(node, prefix, idx) {
    const { num, text } = splitNum(node.label);
    const wrap = el('div', 'form-group');
    wrap.innerHTML = `<div class="form-group-head"><span class="fg-title">${esc(num ? num + '. ' : '')}${esc(text)}</span></div>`;
    if (node.relevant) {
      wrap.dataset.relevant = node.relevant;
      wrap.dataset.prefix = prefix;
    }
    const body = el('div', 'form-group-body');
    if (displayModeOf(node) === 'table') {
      renderGroupTableBody(node, body, prefix);
    } else {
      node.children.forEach((c) => {
        const n = renderNode(c, prefix, idx);
        if (n) body.appendChild(n);
      });
    }
    wrap.appendChild(body);
    return wrap;
  }

  function renderNote(node, prefix) {
    const w = el('div', 'form-note');
    if (node.relevant) {
      w.dataset.relevant = node.relevant;
      w.dataset.prefix = prefix;
    }
    const tpl = esc(node.label) + (node.hint ? '\n' + esc(node.hint) : '');
    w.innerHTML = `<span class="ms">info</span><div data-tpl="${tpl.replace(/"/g, '&quot;')}" data-prefix="${esc(prefix)}"></div>`;
    return w;
  }

  // ------------------------------------------------------- group as table
  function renderGroupTableBody(node, body, prefix) {
    let chunk = [];
    const flush = () => {
      if (!chunk.length) return;
      body.appendChild(renderQuestionsTable(chunk, node, prefix));
      chunk = [];
    };
    node.children.forEach((c) => {
      const chunkable = c.kind === 'question' && !AUTO_TYPES.includes(c.type) && c.type !== 'note' && c.type !== 'calculate';
      if (chunkable) {
        chunk.push(c);
        return;
      }
      flush();
      const n = renderNode(c, prefix, 0);
      if (n) body.appendChild(n);
    });
    flush();
  }

  function renderQuestionsTable(items, groupNode, prefix) {
    const card = el('div', 'table-card');
    card.dataset.search = searchTextOf(groupNode);
    const title = String(groupNode.ui_table_title || splitNum(groupNode.label).text || 'Bảng dữ liệu');
    card.innerHTML = `<div class="table-card-head"><span class="ms">table_view</span><span>${esc(title)}</span></div>`;
    const scroll = el('div', 'table-scroll');
    const table = el('table', 'data-table');
    const thead = el('thead');
    const headRow = el('tr');
    // Bảng này chỉ có đúng một dòng (mỗi câu hỏi là một cột), nên `relevant` của
    // từng câu hỏi ẩn/hiện nguyên cột — gắn lên cả <th> lẫn <td> để tiêu đề và ô
    // dữ liệu không bao giờ lệch nhau.
    items.forEach((c) => {
      const th = el('th');
      th.innerHTML = `${esc(columnLabel(c))}${isRequired(c) ? '<span class="req">*</span>' : ''}`;
      if (c.relevant) {
        th.dataset.relevant = c.relevant;
        th.dataset.prefix = prefix;
      }
      headRow.appendChild(th);
    });
    thead.appendChild(headRow);
    const tbody = el('tbody');
    const tr = el('tr');
    items.forEach((c) => {
      const td = el('td');
      if (c.relevant) {
        td.dataset.relevant = c.relevant;
        td.dataset.prefix = prefix;
      }
      const ctrl = buildControl(c, prefix, 0, { compact: true });
      td.appendChild(ctrl);
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
    table.append(thead, tbody);
    scroll.appendChild(table);
    card.appendChild(scroll);
    if (String(groupNode.ui_table_sum || '') === 'yes') {
      const sumWrap = el('div', 'table-sum');
      const keys = items.map((c) => prefix + c.name);
      sumWrap.dataset.sumkeys = keys.join('|');
      const label = groupNode.ui_table_sum_label || 'Tổng cộng';
      const unit = groupNode.ui_table_sum_unit || '';
      sumWrap.innerHTML = `${esc(label)}: <span class="sumval">0</span>${unit ? ' ' + esc(unit) : ''}`;
      card.appendChild(sumWrap);
    }
    if (groupNode.ui_table_note) {
      card.appendChild(el('div', 'table-note', `<span class="ms">info</span><span>${esc(groupNode.ui_table_note)}</span>`));
    }
    return card;
  }

  const columnLabel = surveyColumnLabel;

  function isRequired(n) {
    return String(n.required || '').toLowerCase() === 'yes';
  }

  // ------------------------------------------------------------- repeats
  /** Bảng dòng cố định: mỗi lựa chọn của list `ui_fixed_list` là một dòng (vd P01–P18). */
  function fixedListOf(node) {
    return String(node.ui_fixed_list || '').trim();
  }

  /** Số dòng do phiếu quyết định (`repeat_count` hoặc dòng cố định) — không có nút thêm/xoá. */
  function isAutoRepeat(node) {
    return !!node.repeat_count || !!fixedListOf(node);
  }

  const hasNamedRows = repeatHasNamedRows;

  // Cột STT chỉ là "1, 2, 3" — cuộn sang phải là mất dấu dòng nào là phân nào, máy
  // nào. Ở cả 7 bảng đánh số hiện có, cột thứ hai là danh mục gọi tên dòng
  // (`fertilizer_name`, `energy_type`...), nên dính luôn cột đó. Bảng "Bản ghi"
  // không cần: tên dòng đã nằm sẵn ở cột đầu.
  function pinsSecondColumn(node) {
    return !hasNamedRows(node);
  }

  // Cột 2 phải dính ngay sau mép phải cột 1, mà bề rộng cột 1 do thuật toán bảng
  // quyết định (co về ~40px khi chật, nhận thêm phần dư khi rộng) nên không viết
  // cứng được trong CSS. Đo thẳng ô tiêu đề và theo dõi nó.
  function trackStickyOffset(table, firstCell) {
    const observer = new ResizeObserver(() => {
      table.style.setProperty('--sticky-col-2-start', `${firstCell.getBoundingClientRect().width}px`);
    });
    observer.observe(firstCell);
    return observer;
  }

  function isLegacyTable(node, prefix) {
    if (prefix) return false; // legacy defaults only apply to top-level-named repeats
    return (LEGACY_TABLE_REPEAT_PARENTS[formKey] || new Set()).has(node.name);
  }

  function repeatDisplayMode(node, prefix) {
    const raw = String(node.ui_display || '').trim();
    if (raw === 'table' || raw === 'cards') return raw;
    return isLegacyTable(node, prefix) ? 'table' : 'cards';
  }

  function renderRepeat(node, parentPfx) {
    const { num, text } = splitNum(node.label);
    const auto = isAutoRepeat(node);
    // Trần số dòng của bảng nhập tay (`ui_max_rows`) — server chặn nộp khi vượt, ở đây khoá nút.
    const maxRows = maxRowsOf(node);
    const mode = repeatDisplayMode(node, parentPfx);
    const wrap = el('div', `repeat repeat-${mode}`);
    wrap.dataset.repeatName = node.name;
    wrap.dataset.parentPrefix = parentPfx;
    wrap.dataset.search = searchTextOf(node);
    if (node.relevant) {
      wrap.dataset.relevant = node.relevant;
      wrap.dataset.prefix = parentPfx;
    }
    wrap.innerHTML = `<div class="repeat-head"><span class="ms">${auto ? 'auto_awesome_motion' : 'repeat'}</span>
      <span class="repeat-title">${esc(num ? num + '. ' : '')}${esc(text)}</span>
      <span class="repeat-badge">${auto ? 'tự động' : 'thủ công'} · <span class="rc">0</span>${maxRows ? `/${maxRows}` : ''} dòng</span></div>`;
    const body = el('div', 'repeat-body');
    wrap.appendChild(body);
    wrap._node = node;
    wrap._tableMode = mode === 'table';
    wrap._maxRows = maxRows;

    if (!auto) {
      const actions = el('div', 'repeat-actions');
      const add = el('button', 'btn btn-tonal sm', '<span class="ms">add</span>Thêm dòng');
      add.type = 'button';
      add.addEventListener('click', () => {
        const cur = manualCounts.get(wrap) ?? countInstances(state, node.name, parentPfx);
        if (cur >= MAX_INSTANCES || (maxRows && cur >= maxRows)) return;
        manualCounts.set(wrap, cur + 1);
        rebuildRepeatBody(wrap);
        recompute();
        fire();
        // Nhập tiếp ngay dòng vừa thêm: bảng vừa dựng lại nên focus đang ở nút, chuột không cần động tới.
        if (wrap._tableMode) {
          wrap.querySelector('tbody tr:last-child')?.querySelector(FOCUSABLE_CELL)?.focus();
        }
      });
      actions.appendChild(add);
      if (maxRows) actions.appendChild(el('span', 'hint-text repeat-max', `Tối đa ${maxRows} dòng`));
      wrap.appendChild(actions);
      wrap._addBtn = add;
    }

    const ph = el('div', 'repeat-empty', '<span class="ms">pending</span><span>Chưa có dòng dữ liệu.</span>');
    wrap.appendChild(ph);

    const initialCount = auto ? 0 : Math.max(1, countInstances(state, node.name, parentPfx));
    if (!auto) manualCounts.set(wrap, initialCount);
    rebuildRepeatBody(wrap, initialCount);
    return wrap;
  }

  function rebuildRepeatBody(wrap, forceCount) {
    const node = wrap._node;
    const parentPfx = wrap.dataset.parentPrefix || '';
    const body = wrap.querySelector('.repeat-body');
    const count = forceCount ?? manualCounts.get(wrap) ?? countInstances(state, node.name, parentPfx);
    body.innerHTML = '';
    // Bảng cũ vừa bị thay cả khối — observer còn giữ nó thì vừa rò bộ nhớ vừa ghi
    // offset vào một bảng không còn trên màn hình.
    wrap._stickyObserver?.disconnect();
    wrap._stickyObserver = null;
    const columns = wrap._tableMode
      ? node.children.filter((c) => c.kind === 'question' && !AUTO_TYPES.includes(c.type) && c.type !== 'note' && c.type !== 'calculate')
      : null;
    let table = null;
    let tbody = null;
    if (wrap._tableMode) {
      const scroll = el('div', 'table-scroll');
      table = el('table', 'data-table repeat-table');
      const thead = el('thead');
      const headRow = el('tr');
      const numbered = !hasNamedRows(node);
      const recordTh = el('th', `record-col${numbered ? ' record-col-num' : ''}`, numbered ? 'STT' : 'Bản ghi');
      headRow.appendChild(recordTh);
      columns.forEach((c, ci) => {
        const th = el('th', ci === 0 && pinsSecondColumn(node) ? 'sticky-col-2' : undefined);
        th.innerHTML = `${esc(columnLabel(c))}${isRequired(c) ? '<span class="req">*</span>' : ''}`;
        headRow.appendChild(th);
      });
      if (!isAutoRepeat(node)) headRow.appendChild(el('th', 'action-col', ''));
      thead.appendChild(headRow);
      tbody = el('tbody');
      table.append(thead, tbody);
      scroll.appendChild(table);
      body.appendChild(scroll);
      attachTableKeyboard(table, wrap);
      if (columns.length && pinsSecondColumn(node)) wrap._stickyObserver = trackStickyOffset(table, recordTh);
    }

    for (let i = 0; i < count; i++) {
      const p = buildPrefix(parentPfx, node.name, i);
      if (wrap._tableMode) {
        tbody.appendChild(buildTableRow(node, columns, p, i, wrap, parentPfx));
      } else {
        body.appendChild(buildCard(node, p, i, wrap, parentPfx));
      }
    }

    const rc = wrap.querySelector('.rc');
    if (rc) rc.textContent = String(count);
    if (wrap._addBtn) wrap._addBtn.disabled = !!wrap._maxRows && count >= wrap._maxRows;
    const emptyEl = wrap.querySelector('.repeat-empty');
    if (emptyEl) emptyEl.hidden = count > 0;
    if (wrap._tableMode && table) table.hidden = count === 0;
  }

  // `ui_row_label`: biểu thức tên dòng, tính trong ngữ cảnh của chính dòng đó — cho bảng mà số dòng
  // không khớp một-một với lựa chọn đã tick (vd `1 + count-selected(...)`: dòng 1 là cà phê, các dòng
  // sau là cây trồng xen đã chọn). Cùng hàm với bản xem lại, để hai màn gọi tên dòng như nhau.
  function rowLabel(node, wrap, idx, parentPfx) {
    const answers = readAnswers();
    return (
      repeatRowLabel(node, idx, parentPfx, answers, choices, evalOpts.listOf, (expr, rowPrefix, index) =>
        evalExpr(answers, expr, rowPrefix, index, choices, formKey, evalOpts),
      ) ?? `Dòng ${idx + 1}`
    );
  }

  /**
   * Bàn phím và định vị trong bảng (Doc/11a): Tab sang phải là của trình duyệt, Enter xuống dòng dưới
   * cùng cột, Enter ở dòng cuối sang nút "Thêm dòng". Dòng và cột đang nhập được tô để không phải dò.
   *
   * Gắn lên `<table>`: bảng bị thay cả khối mỗi lần dựng lại, listener đi cùng nó. Chỉ bắt ô có
   * `data-key`, ô lọc danh sách có Enter riêng của nó.
   */
  function attachTableKeyboard(table, wrap) {
    table.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || e.shiftKey || !e.target.matches?.('input[data-key], select[data-key]')) return;
      const td = e.target.closest('td');
      if (!td || !td.parentElement.classList.contains('repeat-row')) return;
      e.preventDefault();
      for (let row = td.parentElement.nextElementSibling; row; row = row.nextElementSibling) {
        const next = row.cells[td.cellIndex]?.querySelector(FOCUSABLE_CELL);
        if (next && !next.closest('.is-hidden')) {
          next.focus();
          next.select?.();
          return;
        }
      }
      wrap._addBtn?.focus();
    });
    const mark = (td, on) => {
      td.parentElement.classList.toggle('is-active-row', on);
      table.tHead?.rows[0]?.cells[td.cellIndex]?.classList.toggle('is-active-col', on);
    };
    table.addEventListener('focusin', (e) => {
      const td = e.target.closest?.('tbody td');
      if (td) mark(td, true);
    });
    table.addEventListener('focusout', (e) => {
      const td = e.target.closest?.('tbody td');
      if (td) mark(td, false);
    });
  }

  function buildCard(node, prefix, idx, wrap, parentPfx) {
    const card = el('div', 'repeat-instance');
    card.dataset.prefix = prefix;
    card.dataset.idx = String(idx);
    const bar = el('div', 'instance-bar');
    bar.innerHTML = `<span class="inst-index">${idx + 1}</span><span class="inst-title">${esc(rowLabel(node, wrap, idx, parentPfx))}</span>`;
    if (!isAutoRepeat(node)) {
      const rm = el('button', 'btn btn-text sm danger', '<span class="ms">delete</span>Xoá dòng');
      rm.type = 'button';
      rm.addEventListener('click', () => confirmAndRemove(wrap, node, idx, parentPfx));
      bar.appendChild(rm);
    }
    card.appendChild(bar);
    node.children.forEach((c) => {
      const n = renderNode(c, prefix, idx);
      if (n) card.appendChild(n);
    });
    return card;
  }

  function buildTableRow(node, columns, prefix, idx, wrap, parentPfx) {
    const tr = el('tr', 'repeat-row');
    tr.dataset.prefix = prefix;
    tr.dataset.idx = String(idx);
    const numbered = !hasNamedRows(node);
    const first = el('td', `record-col${numbered ? ' record-col-num' : ''}`);
    first.textContent = numbered ? String(idx + 1) : rowLabel(node, wrap, idx, parentPfx);
    // Ô `calculate` không phải là cột của bảng, nhưng phải TỒN TẠI trong DOM thì
    // computeCalcs() mới tính và ghi giá trị vào state. Bỏ hẳn chúng (bản trước) làm
    // mọi `relevant` phụ thuộc biến tính toán — ví dụ `${fertilizer_type} = 'npk'` —
    // rơi vào nhánh "biến chưa từng xuất hiện" của evalRelevant và hiện HẾT các nhánh
    // loại trừ nhau cùng lúc. Giấu trong ô đầu dòng vì `<div>` đặt thẳng vào `<tr>`
    // là HTML sai, trình duyệt sẽ tự đẩy ra ngoài bảng.
    node.children
      .filter((c) => c.kind === 'question' && c.type === 'calculate')
      .forEach((c) => {
        const calcNode = renderQuestionBlock(c, prefix, idx);
        if (calcNode) first.appendChild(calcNode);
      });
    tr.appendChild(first);
    columns.forEach((c, ci) => {
      const td = el('td', ci === 0 && pinsSecondColumn(node) ? 'sticky-col-2' : undefined);
      if (isRequired(c)) td.dataset.requiredCell = '1';
      // Khác bảng câu hỏi một dòng: ở đây `relevant` phụ thuộc prefix của từng dòng
      // nên chỉ ẩn được nội dung ô, không ẩn cả cột — bỏ hẳn <td> sẽ làm lệch lưới.
      if (c.relevant) {
        const cell = el('div', 'cell-relevant');
        cell.dataset.relevant = c.relevant;
        cell.dataset.prefix = prefix;
        cell.appendChild(buildControl(c, prefix, idx, { compact: true }));
        td.appendChild(cell);
      } else {
        td.appendChild(buildControl(c, prefix, idx, { compact: true }));
      }
      tr.appendChild(td);
    });
    if (!isAutoRepeat(node)) {
      const actionTd = el('td', 'action-col');
      const rm = el('button', 'icon-btn danger', '<span class="ms">delete</span>');
      rm.type = 'button';
      rm.title = 'Xoá dòng này';
      rm.addEventListener('click', () => askRemoveInRow(actionTd, rm, wrap, node, idx, parentPfx));
      actionTd.appendChild(rm);
      tr.appendChild(actionTd);
    }
    return tr;
  }

  function rowHasData(node, idx, parentPfx) {
    const prefix = buildPrefix(parentPfx, node.name, idx);
    return Object.entries(state.all()).some(([k, v]) => k.startsWith(prefix) && String(v).trim() !== '');
  }

  function confirmAndRemove(wrap, node, idx, parentPfx) {
    if (rowHasData(node, idx, parentPfx) && !window.confirm('Dòng này đã có dữ liệu. Bạn có chắc muốn xoá?')) return;
    return removeRow(wrap, node, idx, parentPfx);
  }

  /** Bảng: hỏi ngay trong dòng, không popup, để không ngắt nhịp nhập (Doc/11a). */
  function askRemoveInRow(td, rm, wrap, node, idx, parentPfx) {
    // Đọc ô nhập được chứ không đọc `state`: dòng vừa thêm đã có sẵn giá trị của ô tự tính, hỏi ở
    // đó là hỏi thừa ngay lúc người nhập chỉ muốn bỏ một dòng trống.
    const entered = Array.from(td.parentElement.querySelectorAll(FOCUSABLE_CELL)).some(isFilled);
    if (!entered) {
      removeRow(wrap, node, idx, parentPfx);
      return;
    }
    const ask = el(
      'div',
      'row-remove-ask',
      `<span>Xoá dòng ${idx + 1}?</span>
       <button type="button" class="btn btn-text sm danger" data-act="yes">Xoá</button>
       <button type="button" class="btn btn-text sm" data-act="no">Giữ</button>`,
    );
    const keep = () => {
      ask.remove();
      rm.hidden = false;
      rm.focus();
    };
    ask.querySelector('[data-act="yes"]').addEventListener('click', () => removeRow(wrap, node, idx, parentPfx));
    ask.querySelector('[data-act="no"]').addEventListener('click', keep);
    ask.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') keep();
    });
    rm.hidden = true;
    td.appendChild(ask);
    ask.querySelector('[data-act="no"]').focus();
  }

  function removeRow(wrap, node, idx, parentPfx) {
    const removed = removeRepeatRow(state, node.name, idx, parentPfx);
    const cur = manualCounts.get(wrap) ?? countInstances(state, node.name, parentPfx) + 1;
    manualCounts.set(wrap, Math.max(0, cur - 1));
    rebuildRepeatBody(wrap);
    recompute();
    fire();
    return removed;
  }

  // ------------------------------------------------------------ question
  function renderQuestionBlock(n, prefix, idx) {
    if (AUTO_TYPES.includes(n.type)) return null;
    if (n.type === 'calculate') {
      const w = el('div', 'form-field calc-node hidden-calc');
      const key = prefix + n.name;
      w.dataset.key = key;
      w.dataset.calc = n.calculation || '';
      w.dataset.prefix = prefix;
      w.dataset.idx = String(idx);
      return w;
    }
    if (n.type === 'note') return renderNote(n, prefix);

    const { num, text } = splitNum(n.label);
    const req = isRequired(n);
    const key = prefix + n.name;
    const w = el('div', 'q');
    w.dataset.name = n.name;
    w.dataset.key = key;
    w.dataset.prefix = prefix;
    if (req) w.dataset.required = '1';
    if (n.relevant) {
      w.dataset.relevant = n.relevant;
    }
    const number = paper && num ? `<span class="q-num">${esc(num)}.</span> ` : '';
    const labelTpl = number + esc(text) + (req ? '<span class="req" title="Bắt buộc">*</span>' : '');
    const head = el('div', 'q-label-wrap');
    head.innerHTML = `<label class="q-label" data-tpl="${labelTpl.replace(/"/g, '&quot;')}" data-prefix="${esc(prefix)}"></label>
      ${n.hint ? `<p class="q-hint" data-tpl="${esc(n.hint).replace(/"/g, '&quot;')}" data-prefix="${esc(prefix)}"></p>` : ''}`;
    w.appendChild(head);
    w.appendChild(buildControl(n, prefix, idx, {}));
    // Chuỗi để lọc khi tìm kiếm trong phiếu — gộp nhãn, gợi ý và tên biến vì điều
    // tra viên thường nhớ một trong ba, không nhớ chính xác cái nào.
    w.dataset.search = normalizeSearch(`${n.label || ''} ${n.hint || ''} ${n.name || ''}`);
    return w;
  }

  // `default` là hằng số ("0", "1 2 3"...) — trừ khi trông như lời gọi hàm (`today()`),
  // khi đó tính một lần lúc dựng ô. Không đoán rộng hơn: "1 2 3" mà đem đi tính sẽ hỏng.
  function defaultFor(n, key, prefix, idx) {
    if (state.has(key)) return state.get(key);
    const raw = n.default === undefined || n.default === '' ? '' : String(n.default);
    if (!DEFAULT_CALL_RE.test(raw)) return raw;
    const value = evalExpr(readAnswers(), raw, prefix, idx, choices, formKey, evalOpts);
    // Ghi ngay: mặc định đã tính phải lưu như người nhập gõ vào, nếu không ngày phỏng vấn
    // sẽ "trôi" sang hôm sau mỗi lần mở lại phiếu.
    if (value !== '') state.set(key, value);
    return value;
  }

  /**
   * Danh sách động đổi theo master data: MM đã chọn có thể bị xoá mềm sau đó. Không có
   * lựa chọn tương ứng thì ô chọn hiện trống và lượt đọc input kế tiếp xoá mất mã đã lưu
   * mà không ai hay. Giữ lại thành lựa chọn đánh dấu rõ — server sẽ báo lỗi để người
   * nhập tự chọn lại. Danh sách tĩnh giữ nguyên hành vi cũ.
   */
  function withStaleCodes(listName, rows, initial, multiple) {
    if (!isDynamicList(listName) || !initial) return rows;
    const known = new Set(rows.map((r) => String(r.name)));
    const codes = multiple ? selectedList(initial) : [String(initial)];
    const stale = codes.filter((c) => c && !known.has(c));
    return stale.length ? [...rows, ...stale.map((c) => ({ name: c, label: `${c} (không còn trong danh mục)` }))] : rows;
  }

  /**
   * Gắn `choice_filter` lên phần tử chứa lựa chọn. Dạng cũ `category=${x}` giữ đúng
   * đường so `data-category` như trước; dạng khác (vd `selected(${h9_3}, name)`) đánh giá
   * từng lựa chọn bằng bộ phân tích chung.
   */
  function attachChoiceFilter(host, n, prefix, listName) {
    const cf = String(n.choice_filter || '').trim();
    if (!cf) return;
    const legacyVar = legacyChoiceFilterVar(cf);
    if (legacyVar) {
      host.dataset.filterBy = legacyVar;
      return;
    }
    host.dataset.choiceFilter = cf;
    host.dataset.filterList = listName;
    host.dataset.filterPrefix = prefix;
  }

  function unitOf(hint) {
    const m = String(hint || '').match(/Đơn vị:\s*([^\n.]+)/i);
    return m ? m[1].trim().replace(/\s*-\s*.*$/, '') : '';
  }

  // `compact`: ô nằm trong bảng. Phần lớn do CSS của Table Matrix lo; ở đây chỉ giữ ô thả xuống
  // cho câu chọn, vì nút ngang không vừa một ô bảng.
  function buildControl(n, prefix, idx, { compact = false } = {}) {
    const t = n.type;
    const key = prefix + n.name;
    const id = 'f' + (++uid);
    const initial = defaultFor(n, key, prefix, idx);
    // Có `trigger` thì `calculation` chỉ điền sẵn, không khoá ô (xem applyPrefills).
    const prefillFrom = n.calculation && t !== 'calculate' ? triggerName(n) : '';
    const isCalcField = !!(n.calculation && t !== 'calculate' && !prefillFrom);

    if (ATTACHMENT_TYPES.has(t)) {
      const p = el('div', 'field field-disabled');
      p.innerHTML = `<span class="ms">block</span><span>Tệp đính kèm chưa hỗ trợ trong phiên bản này.</span>`;
      return p;
    }
    if (/^geo/.test(t)) {
      const g = el('div', 'field field-geo');
      g.innerHTML = `<span class="ms">my_location</span><span>Toạ độ GPS — thu thập trực tiếp trên thiết bị.</span>`;
      return g;
    }
    if (t.startsWith('select_one') || t.startsWith('select_multiple')) {
      const listName = t.split(/\s+/)[1];
      const multiple = t.startsWith('select_multiple');
      const opts_ = withStaleCodes(listName, choices[listName] || [], initial, multiple);
      const wrap = el('div', 'field');
      if (multiple) {
        const chosen = new Set(selectedList(initial));
        const group = el('div', 'opts');
        opts_.forEach((o) => {
          const lab = el('label', 'opt');
          if (o.category) lab.dataset.category = o.category;
          lab.innerHTML = `<input type="checkbox" name="${esc(key)}" value="${esc(o.name)}" data-key="${esc(key)}" data-name="${esc(n.name)}" ${chosen.has(String(o.name)) ? 'checked' : ''}>
            <span class="opt-text">${esc(o.label)}</span>`;
          group.appendChild(lab);
        });
        // Dạng cũ chưa từng lọc ô chọn nhiều — chỉ dạng mới mới lọc ở đây.
        if (n.choice_filter && !legacyChoiceFilterVar(n.choice_filter)) attachChoiceFilter(group, n, prefix, listName);
        group.addEventListener('change', () => onControlInput(key));
        wrap.appendChild(group);
      } else if ((paper && !compact ? paperSelectMode(n, opts_) : selectOneMode(n)) === 'radio') {
        wrap.appendChild(buildRadioGroup(n, key, initial, opts_, prefix, listName));
      } else {
        const sel = el('select');
        sel.id = id;
        sel.dataset.key = key;
        sel.dataset.name = n.name;
        sel.appendChild(new Option('— Chọn —', ''));
        opts_.forEach((o) => {
          const op = new Option(o.label, o.name);
          if (o.category) op.dataset.category = o.category;
          sel.appendChild(op);
        });
        sel.value = initial;
        attachChoiceFilter(sel, n, prefix, listName);
        sel.addEventListener('change', () => onControlInput(key));
        if (shouldShowOptionSearch(n, opts_.length)) {
          wrap.classList.add('field-searchable');
          wrap.appendChild(buildOptionSearch(sel, opts_.length));
        }
        wrap.appendChild(sel);
      }
      // Ràng buộc trên câu chọn (vd "Không có" không đi cùng mục khác) — trước lát 3 câu
      // chọn không có ràng buộc nào chạy được nên không gắn.
      if (n.constraint) {
        wrap.dataset.constraint = n.constraint;
        wrap.dataset.constraintKey = key;
        wrap.dataset.constraintPrefix = prefix;
      }
      wrap.appendChild(el('div', 'field-error', ''));
      return wrap;
    }

    // text / integer / decimal / date
    const wrap = el('div', 'field');
    const type = t === 'date' ? 'date' : NUMERIC_TYPES.has(t) ? 'number' : 'text';
    const step = t === 'decimal' ? '0.01' : t === 'integer' ? '1' : null;
    const cmax = NUMERIC_TYPES.has(t) ? constraintMax(n.constraint) : '';
    const unit = unitOf(n.hint);
    const input = document.createElement('input');
    input.id = id;
    input.type = type;
    if (step) input.step = step;
    if (isCalcField) input.readOnly = true;
    if (cmax) {
      input.max = cmax;
      input.min = '0';
    }
    input.dataset.key = key;
    input.dataset.name = n.name;
    input.value = initial;
    if (n.constraint) {
      input.dataset.constraint = n.constraint;
      input.dataset.constraintPrefix = prefix;
    }
    if (isCalcField) {
      input.dataset.calc = n.calculation;
      input.dataset.prefix = prefix;
      input.dataset.idx = String(idx);
      wrap.classList.add('calc-field');
    }
    if (prefillFrom) {
      input.dataset.prefill = n.calculation;
      input.dataset.trigger = prefillFrom;
      input.dataset.prefix = prefix;
      input.dataset.idx = String(idx);
      // Ghi nhận giá trị hiện tại của câu kích hoạt ngay lúc dựng ô: mở lại phiếu
      // hay dựng lại bảng lặp không được coi là "vừa đổi" rồi đè số người nhập đã sửa.
      input._lastTrigger = String(resolve(readAnswers(), prefillFrom, prefix) ?? '');
    }
    input.addEventListener('input', () => onControlInput(key));
    input.addEventListener('change', () => onControlInput(key));
    wrap.appendChild(input);
    if (unit) wrap.appendChild(el('span', 'field-suffix', esc(unit)));
    wrap.appendChild(el('div', 'field-error', ''));
    return wrap;
  }

  /**
   * Nút radio: mọi lựa chọn hiện sẵn, bấm một phát là xong — nhanh hơn danh sách
   * thả xuống khi phỏng vấn tại vườn, nhưng chỉ hợp với danh sách ngắn (Có/Không,
   * mức độ…), nên đây là lựa chọn của người soạn phiếu chứ không tự suy ra.
   *
   * Câu hỏi không bắt buộc có thêm nút "Bỏ chọn": radio không thể tự bỏ chọn bằng
   * cách bấm lại, mà đã lỡ tay chọn thì phải có đường lui.
   */
  function buildRadioGroup(n, key, initial, opts_, prefix, listName) {
    const chosen = String(initial ?? '');
    const group = el('div', 'opts opts-radio');
    group.setAttribute('role', 'radiogroup');
    attachChoiceFilter(group, n, prefix, listName);
    opts_.forEach((o) => {
      const lab = el('label', 'opt');
      if (o.category) lab.dataset.category = o.category;
      // `chosen !== ''` là bắt buộc: có danh sách chứa lựa chọn mang mã rỗng
      // (vd "Không có chứng nhận"), thiếu điều kiện này thì câu chưa trả lời sẽ
      // tự hiện ra như đã chọn mục đó.
      const checked = chosen !== '' && chosen === String(o.name);
      lab.innerHTML = `<input type="radio" name="${esc(key)}" value="${esc(o.name)}" data-key="${esc(key)}" data-name="${esc(n.name)}" ${checked ? 'checked' : ''}>
        <span class="opt-text">${esc(o.label)}</span>`;
      group.appendChild(lab);
    });

    let clear = null;
    if (!isRequired(n)) {
      clear = el('button', 'btn btn-text sm opt-clear', '<span class="ms">backspace</span>Bỏ chọn');
      clear.type = 'button';
      clear.hidden = !chosen;
      clear.addEventListener('click', () => {
        group.querySelectorAll('input[type="radio"]').forEach((r) => {
          r.checked = false;
        });
        clear.hidden = true;
        onControlInput(key);
      });
      group.appendChild(clear);
    }

    group.addEventListener('change', () => {
      if (clear) clear.hidden = false;
      onControlInput(key);
    });
    return group;
  }

  /**
   * Ô lọc nhanh cho danh sách thả xuống dài (loại phân bón, loại thuốc BVTV… có
   * hàng chục dòng). Chỉ ẩn `<option>` không khớp — không đổi giá trị đang chọn,
   * nên không bao giờ làm mất câu trả lời đã nhập.
   */
  function buildOptionSearch(sel, total) {
    const box = el('div', 'opt-search');
    const input = document.createElement('input');
    input.type = 'search';
    input.placeholder = `Lọc trong ${total} lựa chọn…`;
    input.setAttribute('aria-label', 'Lọc danh sách lựa chọn');
    input.addEventListener('input', () => {
      const q = normalizeSearch(input.value);
      Array.from(sel.options).forEach((o) => {
        if (!o.value) return;
        // Lựa chọn đang được chọn luôn hiển thị, nếu không select sẽ trông như rỗng.
        const keep = !q || o.selected || normalizeSearch(o.textContent).includes(q);
        o.hidden = !keep;
      });
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });
    box.appendChild(input);
    return box;
  }

  // Đọc lại toàn bộ input rồi tính lại — `_key` giữ trong chữ ký để nơi gọi nói
  // rõ ô nào vừa đổi, dù hiện chưa dùng để tối ưu phạm vi tính lại.
  function onControlInput(_key) {
    readAllInputs();
    recompute();
    fire();
  }

  // -------------------------------------------------------------- cycle
  function readAllInputs() {
    const multi = {};
    container.querySelectorAll('input[data-key], select[data-key]').forEach((inp) => {
      const k = inp.dataset.key;
      if (inp.type === 'checkbox') {
        if (!Array.isArray(multi[k])) multi[k] = [];
        if (inp.checked) multi[k].push(inp.value);
      } else if (inp.type === 'radio') {
        if (!(k in multi)) multi[k] = '';
        if (inp.checked) multi[k] = inp.value;
      } else {
        multi[k] = inp.value;
      }
    });
    Object.keys(multi).forEach((k) => {
      state.set(k, Array.isArray(multi[k]) ? multi[k].join(' ') : multi[k]);
    });
  }

  function computeCalcs() {
    container.querySelectorAll('.calc-field input[data-calc], .calc-node[data-calc]').forEach((elm) => {
      const isInput = elm.tagName === 'INPUT';
      const dataHost = isInput ? elm : elm;
      const calc = dataHost.dataset.calc;
      const prefix = dataHost.dataset.prefix || '';
      const idx = parseInt(dataHost.dataset.idx || '0', 10);
      const value = evalExpr(readAnswers(), calc, prefix, idx, choices, formKey, evalOpts);
      state.set(dataHost.dataset.key, value);
      if (isInput) elm.value = value;
    });
  }

  /**
   * Ô điền sẵn (`calculation` + `trigger`, như ODK): mỗi khi câu kích hoạt đổi giá
   * trị — VD chọn tên thuốc — thì tính `calculation` và điền vào ô, đè cả số cũ. Ngoài
   * lúc đó ô là ô nhập tay bình thường, người nhập sửa lại theo thực tế được.
   */
  function applyPrefills() {
    container.querySelectorAll('input[data-prefill]').forEach((inp) => {
      const prefix = inp.dataset.prefix || '';
      const cur = String(resolve(readAnswers(), inp.dataset.trigger, prefix) ?? '');
      if (cur === inp._lastTrigger) return;
      inp._lastTrigger = cur;
      if (inp.readOnly || inp.disabled) return;
      const idx = parseInt(inp.dataset.idx || '0', 10);
      const value = evalExpr(readAnswers(), inp.dataset.prefill, prefix, idx, choices, formKey, evalOpts);
      inp.value = value;
      state.set(inp.dataset.key, value);
    });
  }

  function syncAutoRepeats() {
    let changed = false;
    container.querySelectorAll('.repeat[data-repeat-name]').forEach((wrap) => {
      const node = wrap._node;
      if (!node || !isAutoRepeat(node)) return;
      const parentPfx = wrap.dataset.parentPrefix || '';
      // Dòng cố định thắng `repeat_count` — cùng thứ tự với instanceCount() của audit-rules.
      const fixed = fixedListOf(node);
      const raw = fixed
        ? String((choices[fixed] || []).length)
        : evalExpr(readAnswers(), node.repeat_count, parentPfx, 0, choices, formKey, evalOpts);
      const { want } = clampAutoCount(raw);
      const body = wrap.querySelector('.repeat-body');
      const have = wrap._tableMode
        ? body.querySelectorAll('tbody > tr').length
        : body.children.length;
      if (have !== want) {
        rebuildRepeatBody(wrap, want);
        changed = true;
      }
    });
    return changed;
  }

  function interp(tpl, prefix) {
    return String(tpl || '').replace(/\$\{\s*([\w- ]+)\s*\}/g, (m, name) => {
      const v = resolve(readAnswers(), name.trim(), prefix);
      if (v !== undefined && v !== '') return esc(v);
      // Khoá master data trống nghĩa là hồ sơ Hộ/Vườn/MM chưa có thông tin đó — nói vậy với
      // điều tra viên thay vì hiện tên biến (`plot_gps`) như với câu hỏi chưa trả lời.
      if (masterKeys.has(name.trim())) return '<span class="var-ref">— chưa có trong hồ sơ</span>';
      return `<span class="var-ref">${esc(name.trim())}</span>`;
    });
  }

  function renderDynamicText() {
    container.querySelectorAll('[data-tpl]').forEach((n) => {
      const prefix = n.dataset.prefix ?? '';
      const html = interp(n.dataset.tpl, prefix);
      if (n._tplCache !== html) {
        n._tplCache = html;
        n.innerHTML = html;
      }
    });
  }

  /**
   * Ẩn/hiện theo `relevant`, lặp tới khi tập câu ẩn đứng yên: mỗi vòng đọc các câu ẩn của
   * vòng trước là rỗng (`readAnswers`), nên A = Không ẩn B rồi ẩn tiếp C, D đứng sau B. Tập ẩn
   * tính lại từ đầu mỗi vòng — câu `${b} != '1'` phải hiện lại khi B bị coi là rỗng.
   * Trả `true` khi tập câu ẩn đổi (để vẽ lại phần chữ chèn biến).
   */
  function applyRelevance() {
    let changed = false;
    for (let pass = 0; pass < RELEVANCE_PASSES; pass++) {
      const answers = readAnswers();
      const nextHidden = new Set();
      container.querySelectorAll('[data-relevant]').forEach((n) => {
        const prefix = n.dataset.prefix ?? '';
        const visible = evalRelevant(answers, n.dataset.relevant, prefix, evalOpts);
        n.classList.toggle('is-hidden', !visible);
        if (!visible) {
          if (n.dataset.key) nextHidden.add(n.dataset.key);
          n.querySelectorAll('[data-key]').forEach((d) => nextHidden.add(d.dataset.key));
        }
      });
      const same = nextHidden.size === hiddenKeys.size && [...nextHidden].every((k) => hiddenKeys.has(k));
      hiddenKeys = nextHidden;
      if (same) break;
      changed = true;
      computeCalcs(); // ô tính đọc câu vừa ẩn thành rỗng
    }
    markInapplicableCells();
    return changed;
  }

  /**
   * Ô tắt theo `relevant` của dòng: hiện "không áp dụng" gạch chéo thay vì ô trống, để người đối chiếu
   * với giấy biết ô đó không hỏi chứ không phải quên nhập. Nội dung ô đã `display: none` nên không
   * nhận focus.
   */
  function markInapplicableCells() {
    container.querySelectorAll('.cell-relevant').forEach((cell) => {
      const na = cell.classList.contains('is-hidden');
      const td = cell.parentElement;
      td.classList.toggle('cell-na', na);
      if (na) td.title = 'Không áp dụng cho dòng này';
      else td.removeAttribute('title');
    });
  }

  /** Ô bắt buộc còn trống trong dòng đã có dữ liệu viền đỏ ngay, không đợi tới lúc nộp (Doc/11a). */
  function markIncompleteRows() {
    container.querySelectorAll('tr.repeat-row').forEach((tr) => {
      const started = Array.from(tr.querySelectorAll(FOCUSABLE_CELL)).some(isFilled);
      tr.querySelectorAll('td[data-required-cell]').forEach((td) => {
        const own = Array.from(td.querySelectorAll('input[data-key], select[data-key]'));
        td.classList.toggle('cell-missing', started && !td.classList.contains('cell-na') && !own.some(isFilled));
      });
    });
  }

  /**
   * Bỏ hẳn các khoá máy chủ vừa cắt vì câu đang ẩn (`prunedKeys` của autosave): xoá khỏi
   * trạng thái và làm trống ô nhập, để khi câu hiện lại thì nó trống — đúng như dữ liệu đã lưu
   * — chứ không hiện số cũ mà máy chủ không còn giữ.
   */
  function forgetKeys(keys) {
    if (!keys?.length) return;
    // Khoá người nhập vừa sửa lại sau lần lưu đó (chưa gửi) là giá trị MỚI — bật lại nhánh
    // và gõ trong lúc chờ máy chủ trả lời. Giữ nó; lần lưu kế tiếp sẽ gửi đi.
    const unsent = state.getDirty();
    for (const key of keys) {
      if (key in unsent) continue;
      state.forget(key);
      container.querySelectorAll(`[data-key="${CSS.escape(key)}"]`).forEach((el) => {
        if (el.type === 'checkbox' || el.type === 'radio') el.checked = false;
        else if ('value' in el && el.tagName !== 'DIV') el.value = '';
      });
    }
    recompute();
  }

  function applyChoiceFilters() {
    container.querySelectorAll('select[data-filter-by]').forEach((sel) => {
      const prefix = sel.closest('[data-prefix]')?.dataset.prefix || '';
      const filterName = sel.dataset.filterBy;
      const filterValue = resolve(readAnswers(), filterName, prefix);
      Array.from(sel.options).forEach((o) => {
        if (!o.value) return;
        const ok = !filterValue || !o.dataset.category || o.dataset.category === filterValue;
        o.hidden = !ok;
        o.disabled = !ok;
      });
      if (sel.selectedOptions[0] && sel.selectedOptions[0].hidden) {
        sel.value = '';
        state.set(sel.dataset.key, '');
      }
    });
    // Cùng luật `choice_filter` nhưng trên nhóm nút radio: ẩn hẳn nhãn thay vì
    // ẩn `<option>`. Không đụng tới `disabled` — thuộc tính đó thuộc về
    // ui/readonly.js, ghi đè ở đây sẽ mở khoá phiếu đã nộp.
    container.querySelectorAll('.opts-radio[data-filter-by]').forEach((group) => {
      const prefix = group.closest('[data-prefix]')?.dataset.prefix || '';
      const filterValue = resolve(readAnswers(), group.dataset.filterBy, prefix);
      let cleared = false;
      group.querySelectorAll('label.opt').forEach((lab) => {
        const cat = lab.dataset.category;
        const ok = !filterValue || !cat || cat === filterValue;
        lab.hidden = !ok;
        const input = lab.querySelector('input');
        if (!ok && input?.checked) {
          input.checked = false;
          state.set(input.dataset.key, '');
          cleared = true;
        }
      });
      if (cleared) {
        const clear = group.querySelector('.opt-clear');
        if (clear) clear.hidden = true;
      }
    });
    applyExprChoiceFilters();
  }

  /**
   * `choice_filter` dạng biểu thức: đánh giá từng lựa chọn (`name`, `label`, `category` là
   * cột của dòng lựa chọn). Lựa chọn bị lọc mà đang được chọn thì bỏ chọn — như dạng cũ.
   */
  function applyExprChoiceFilters() {
    const answers = readAnswers();
    container.querySelectorAll('[data-choice-filter]').forEach((host) => {
      const rows = choices[host.dataset.filterList] || [];
      const byName = new Map(rows.map((r) => [String(r.name), r]));
      const prefix = host.dataset.filterPrefix || '';
      const allowed = (value) => {
        const row = byName.get(String(value));
        return !row || choiceAllowed(host.dataset.choiceFilter, row, answers, prefix, evalOpts);
      };
      if (host.tagName === 'SELECT') {
        Array.from(host.options).forEach((o) => {
          if (!o.value) return;
          const ok = allowed(o.value);
          o.hidden = !ok;
          o.disabled = !ok;
        });
        if (host.selectedOptions[0] && host.selectedOptions[0].hidden) {
          host.value = '';
          state.set(host.dataset.key, '');
        }
        return;
      }
      // Nhóm radio/checkbox: ẩn nhãn, không đụng `disabled` (thuộc ui/readonly.js).
      let changed = false;
      host.querySelectorAll('label.opt').forEach((lab) => {
        const input = lab.querySelector('input');
        if (!input) return;
        const ok = allowed(input.value);
        lab.hidden = !ok;
        if (!ok && input.checked) {
          input.checked = false;
          changed = true;
        }
      });
      if (!changed) return;
      const inputs = Array.from(host.querySelectorAll('input'));
      const key = inputs[0]?.dataset.key;
      if (key) state.set(key, inputs.filter((i) => i.checked).map((i) => i.value).join(' '));
      const clear = host.querySelector('.opt-clear');
      if (clear && !inputs.some((i) => i.checked)) clear.hidden = true;
    });
  }

  function constraintCtx(prefix) {
    return { ...evalOpts, answers: readAnswers(), prefix };
  }

  function validateConstraints() {
    container.querySelectorAll('input[data-constraint]').forEach((inp) => {
      const computed = inp.readOnly;
      const blank = inp.value === '' || parseFloat(inp.value) === 0;
      const ok =
        computed && blank
          ? true
          : checkConstraint(inp.dataset.constraint, inp.value, constraintCtx(inp.dataset.constraintPrefix || ''));
      inp.closest('.field')?.classList.toggle('invalid', !ok);
      const err = inp.closest('.field')?.querySelector('.field-error');
      if (err) err.textContent = ok ? '' : 'Giá trị không hợp lệ';
    });
    container.querySelectorAll('.field[data-constraint-key]').forEach((field) => {
      const value = String(state.get(field.dataset.constraintKey) ?? '');
      const ok = checkConstraint(field.dataset.constraint, value, constraintCtx(field.dataset.constraintPrefix || ''));
      field.classList.toggle('invalid', !ok);
      const err = field.querySelector(':scope > .field-error');
      if (err) err.textContent = ok ? '' : 'Giá trị không hợp lệ';
    });
  }

  function updateSumFooters() {
    container.querySelectorAll('[data-sumkeys]').forEach((elm) => {
      const sum = elm.dataset.sumkeys.split('|').reduce((a, k) => a + (parseFloat(state.get(k)) || 0), 0);
      const out = elm.querySelector('.sumval');
      if (out) out.textContent = (Math.round(sum * 100) / 100).toLocaleString('vi-VN');
    });
  }

  function recompute() {
    for (let pass = 0; pass < 5; pass++) {
      computeCalcs();
      if (!syncAutoRepeats()) break;
    }
    applyPrefills();
    computeCalcs();
    renderDynamicText();
    if (applyRelevance()) renderDynamicText();
    applyChoiceFilters();
    validateConstraints();
    updateSumFooters();
    markIncompleteRows();
  }

  function getVisibleAnswers() {
    const all = state.all();
    const out = {};
    Object.keys(all).forEach((k) => {
      if (!hiddenKeys.has(k)) out[k] = all[k];
    });
    return out;
  }

  /**
   * @deprecated Đếm bằng DOM nên bỏ sót mọi câu hỏi hiển thị trong Table Matrix (chúng
   * là `<td>`, không có khối `.q[data-required]`) — thanh tiến độ vì thế báo đủ trong
   * khi nhóm lặp vẫn còn ô bắt buộc trống. Dùng `requiredProgress()` của
   * @farmer/audit-rules: nó đếm đúng tập câu hỏi mà server dùng để chặn nộp phiếu.
   * Giữ lại vì là API công khai của renderer; xoá khi chắc không còn ai gọi.
   */
  function getProgress() {
    let total = 0;
    let done = 0;
    container.querySelectorAll('.q[data-required]').forEach((q) => {
      if (q.classList.contains('is-hidden') || q.closest('.is-hidden')) return;
      total++;
      const v = state.get(q.dataset.key);
      if (String(v).trim() !== '') done++;
    });
    return { total, done };
  }

  // ------------------------------------------------------- view controls
  /** Mục lục cho thanh điều hướng bên trái. Gọi sau `mount()`. */
  function getSections() {
    return Array.from(container.querySelectorAll('.form-section')).map((sec) => ({
      id: sec.id,
      num: sec.dataset.sectionNum || '',
      text: sec.dataset.sectionText || '',
      el: sec,
    }));
  }

  function setAllCollapsed(collapsed) {
    container.querySelectorAll('.form-section').forEach((sec) => {
      sec.classList.toggle('collapsed', collapsed);
      sec.querySelector('.form-section-head')?.setAttribute('aria-expanded', String(!collapsed));
    });
  }

  /**
   * Lọc câu hỏi theo từ khoá. Trả về số câu khớp; chuỗi rỗng thì bỏ lọc.
   * Phần nào không còn câu hỏi khớp sẽ được ẩn để mục lục không dẫn tới chỗ trống.
   */
  function setSearch(query) {
    const q = normalizeSearch(query);
    container.classList.toggle('is-filtering', !!q);
    let matches = 0;
    // Khối tìm được: câu hỏi đơn, bảng câu hỏi và nhóm lặp. Bảng/nhóm lặp được
    // giữ hoặc bỏ nguyên khối vì từng cột không tồn tại như một phần tử riêng.
    container.querySelectorAll('[data-search]').forEach((el_) => {
      const hit = !q || el_.dataset.search.includes(q);
      el_.classList.toggle('search-miss', !hit);
      if (hit) matches++;
    });
    container.querySelectorAll('.form-section').forEach((sec) => {
      if (!q) {
        sec.classList.remove('search-empty');
        return;
      }
      // Khớp cả tên phần: gõ "phân bón" phải mở được phần VII dù nội dung nằm
      // trong nhóm lặp.
      const sectionHit = (sec.dataset.sectionSearch || '').includes(q);
      const childHit = sec.querySelector('[data-search]:not(.search-miss)');
      if (sectionHit) {
        sec.querySelectorAll('[data-search]').forEach((el_) => el_.classList.remove('search-miss'));
      }
      const keep = sectionHit || !!childHit;
      sec.classList.toggle('search-empty', !keep);
      if (keep) sec.classList.remove('collapsed');
    });
    return matches;
  }

  return {
    mount,
    recompute,
    getVisibleAnswers,
    getProgress,
    hiddenKeys: () => hiddenKeys,
    forgetKeys,
    getSections,
    setAllCollapsed,
    setSearch,
  };
}
